import { Settings, LogOut, Sun, Moon, Shield } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { Button } from './ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import type { User as UserType } from '../lib/types'

interface UserProfileDropdownProps {
  user: UserType
  onLogout: () => void
  onSettingsClick: () => void
  onAdminClick?: () => void
}

function UserAvatar({ user }: { user: UserType }) {
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ')
    if (parts.length === 0) return 'U'
    if (parts.length === 1) return parts[0][0].toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }

  const initials = user.name ? getInitials(user.name) : (user.email?.[0] || 'U').toUpperCase()

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-emerald-500 to-green-500 text-white text-base font-medium">
      {initials}
    </div>
  )
}

export function UserProfileDropdown({ user, onLogout, onSettingsClick, onAdminClick }: UserProfileDropdownProps) {
  const { theme, setTheme } = useTheme()

  const handleLogout = () => {
    onLogout()
  }

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full hover:bg-white/10"
        >
          <UserAvatar user={user} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name || 'User'}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={onSettingsClick}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>
          {user.is_admin && onAdminClick && (
            <DropdownMenuItem onClick={onAdminClick}>
              <Shield className="mr-2 h-4 w-4" />
              <span>Admin Portal</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={toggleTheme}>
            {theme === 'dark' ? (
              <Sun className="mr-2 h-4 w-4" />
            ) : (
              <Moon className="mr-2 h-4 w-4" />
            )}
            <span>Toggle theme</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}